import { ProjectStatus } from '~/constants';
import { Project } from '~/types/project';
import { Portfolio } from '~/types/portfolio';

export default function dealSummary(portfolio: Portfolio, projectBlobs: any) {
  const parentProjectBlobs = projectBlobs?.filter((projectBlob: any) => !projectBlob?.project?.parentId);

  // Get list of bid amounts for each parentProjectBlob?.projectDisplayValues.dealSummary
  const projectBids = parentProjectBlobs?.map((projectBlob: any) => {
    return {
      projectId: projectBlob?.project?.id,
      name: projectBlob?.project?.name,
      bids: projectBlob?.projectDisplayValues?.dealSummary?.projectBidData
    };
  });

  let investorQueue: any[] = [];
  portfolio?.projects?.forEach((project: Project) => {
    if (!project.parentId) {
      investorQueue = project.investorQueue || [];
    }
  });
  const investorProjectMap = portfolio?.projects?.reduce((acc: any, project: Project) => {
    if (!project.parentId) {
      return acc;
    }
    if (project?.parentId) {
      if (project?.id && project?.investorId) {
        acc[project.investorId] = acc[project.investorId] || {};
        acc[project.investorId].projects = acc[project.investorId].projects || {};
        acc[project.investorId].projects[project.parentId] = project;
        acc[project.investorId].projects[project.parentId].investorQueue = investorQueue;
        if (project?.investor?.company?.name) {
          acc[project.investorId].investor = project?.investor;
        }
      }
      return acc;
    }
    return acc;
  }, {});

  if (investorProjectMap) {
    Object.values(investorProjectMap).forEach((row: any) => {
      const projectList = Object.values(row?.projects ?? {});
      if (projectList.length) {
        // Set conversation ended status if all projects are declined
        const declined = projectList.filter((p: any) => p.status === ProjectStatus.BID_REJECTED || p.status === ProjectStatus.DECLINED_TO_BID);
        if (declined?.length === projectList.length) {
          row.conversationEnded = true;
        }
      }
    });
  }

  // Create map of projectIds to investorId to projectBids and sellerFees
  const projectDataMap = parentProjectBlobs?.reduce((acc: any, projectBlob: any) => {
    const projectId = projectBlob?.project?.id;

    // Find matching project bids
    const projectBid = projectBids?.find((bid: any) => bid.projectId === projectId);

    const investorBidsMap = projectBid?.bids?.reduce((bidAcc: any, bid: any) => {
      bidAcc[bid.investorId] = bid;
      return bidAcc;
    }, {}) || {};

    // Store the map by projectId
    acc[projectId] = investorBidsMap;

    return acc;
  }, {});

  // Create map of investorId to projectId to bids and sellerFees
  const investorProjectBidMap = Object.entries(projectDataMap)?.reduce((acc: any, [projectId, investorBidAmountsMap]: any) => {
    Object.entries(investorBidAmountsMap)?.forEach(([investorId, projectBidAmount]: any) => {
      acc[investorId] = acc[investorId] || {};
      acc[investorId][projectId] = projectBidAmount;
    });
    return acc;
  }, {});

  // Create a Set of investors from portfolio?.projects and then create a sorted array from the Set
  const investorActivityByCompanyId = (portfolio as any).investorActivityByCompanyId as Record<number, any> | undefined;

  let investors = Array.from(
    new Set(
      portfolio?.projects
        ?.filter((p: Project) => !!p.parentId)
        ?.map((project: Project) => (
          JSON.stringify({
            id: project?.investor?.id,
            name: project?.investor?.company?.name,
            logo: project?.investor?.company?.logoS3Url,
            companyId: project?.investor?.company?.id,
          }))
        )
    )
  )
    .map((json: any) => JSON.parse(json))
    .sort((a: any, b: any) => a.name.localeCompare(b.name));

  if (investorActivityByCompanyId) {
    investors = investors.map((inv: any) => ({
      ...inv,
      activity: investorActivityByCompanyId[inv.companyId] ?? null,
    }));
  }

  return { investors, projectBids, investorProjectMap, investorProjectBidMap };
}